import { useEffect, useState } from 'react';

import { useQuery } from '@tanstack/react-query';

import AIJob from 'models/AIJob';
import { aiJobQueryKeys, getAIJob } from 'services/api/aiJobs';
import { useToastActions } from 'stores/toastStore';

type UseAIJobPollingOptions = {
  aiJobId?: string;
  onPollingIntervalSuccess?: (job: AIJob) => void;
  onFinished?: (job: AIJob) => void;
};

export default function useAIJobPolling({ aiJobId, onPollingIntervalSuccess, onFinished }: UseAIJobPollingOptions) {
  const { addToast } = useToastActions();
  const [isPolling, setIsPolling] = useState(false);

  useEffect(() => {
    if (aiJobId) {
      setIsPolling(true);
    } else {
      setIsPolling(false);
    }
  }, [aiJobId]);

  const { data: polledJob, isError } = useQuery({
    queryKey: aiJobQueryKeys.show(aiJobId || ''),
    queryFn: () => getAIJob(aiJobId || ''),
    enabled: isPolling && !!aiJobId,
    refetchInterval: 1500,
    refetchIntervalInBackground: true,
  });

  useEffect(() => {
    if (polledJob) {
      onPollingIntervalSuccess?.(polledJob);

      if (!polledJob.isProcessing) {
        setIsPolling(false);
        onFinished?.(polledJob);
      }
    }
  }, [polledJob, onFinished, onPollingIntervalSuccess]);

  useEffect(() => {
    if (isError) {
      setIsPolling(false);
      addToast({ text: 'An error occurred. Please refresh the page.' });
    }
  }, [isError, addToast]);

  return {
    polledJob,
    isPolling,
    pollingError: isError,
  };
}
